import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { CONTACT } from "@/lib/data";

export const SectionLabel = ({ children, dark = false }) => (
  <span
    className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] ${
      dark ? "text-paper/60" : "text-gold"
    }`}
  >
    <span className={`h-px w-8 ${dark ? "bg-paper/40" : "bg-gold"}`} />
    {children}
  </span>
);

export const PageHero = ({ label, title, subtitle }) => (
  <section className="relative z-10 pt-40 pb-16 lg:pt-48 lg:pb-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <SectionLabel>{label}</SectionLabel>
      </Reveal>
      <Reveal delay={0.08}>
        <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02] text-forest max-w-4xl">
          {title}
        </h1>
      </Reveal>
      {subtitle && (
        <Reveal delay={0.16}>
          <p className="mt-8 max-w-2xl text-lg sm:text-xl leading-relaxed text-ink-soft">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const EnquiryBanner = () => (
  <section data-testid="enquiry-banner" className="relative z-10 py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="relative overflow-hidden rounded-sm bg-forest px-8 py-16 lg:px-20 lg:py-24">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-forest-light/40 blur-3xl" />
        <div className="relative max-w-3xl">
          <SectionLabel dark>Let&rsquo;s talk supply</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-paper">
            Looking for dependable paper &amp; board supply?
          </h2>
          <p className="mt-6 text-lg text-paper/70 leading-relaxed">
            Whether it&rsquo;s the right stock for a print run or bulk board for packaging, talk to
            Mysore Paper today. Fast answers, honest advice, consistent supply.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="banner-whatsapp-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper px-8 py-4 font-semibold text-forest transition-colors duration-300 hover:bg-paper-soft"
            >
              Send an Enquiry
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={`tel:${CONTACT.phoneMobileRaw}`}
              data-testid="banner-call-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-paper/30 px-8 py-4 font-semibold text-paper transition-colors duration-300 hover:bg-paper/10"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phoneMobile}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const PrimaryLink = ({ to, href, children, testId, secondary = false }) => {
  const cls = secondary
    ? "group inline-flex items-center gap-2 rounded-full border border-forest px-7 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-paper-soft"
    : "group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-forest-deep";
  const inner = (
    <>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  if (href) return <a href={href} data-testid={testId} className={cls}>{inner}</a>;
  return <Link to={to} data-testid={testId} className={cls}>{inner}</Link>;
};
